/**
 * KnowledgeGapIndicator - Shows what information the agent couldn't find
 * 
 * Design principles:
 * - Non-alarming but noticeable (amber/yellow tones)
 * - Actionable: suggests where to find information
 * - Collapsible for cleaner UI when there are multiple gaps
 * - Encourages user to provide missing information
 */

import React, { useState } from 'react';
import type { KnowledgeGap } from '../../../api/services/agent';

interface KnowledgeGapIndicatorProps {
  gaps: KnowledgeGap[];
  onSuggestExpert?: () => void;
  onUploadDocument?: () => void;
  className?: string;
}

const SourceSuggestionIcon: React.FC<{ source: KnowledgeGap['suggested_source'] }> = ({ source }) => {
  switch (source) {
    case 'web':
      return (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      );
    case 'expert':
      return (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case 'user':
      return (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      );
    default:
      return (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

const getSourceLabel = (source: KnowledgeGap['suggested_source']): string => {
  switch (source) {
    case 'web': return 'Try web search';
    case 'expert': return 'Ask a colleague';
    case 'user': return 'You may know this';
    default: return 'Unknown source';
  }
};

export const KnowledgeGapIndicator: React.FC<KnowledgeGapIndicatorProps> = ({
  gaps,
  onSuggestExpert,
  onUploadDocument,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!gaps || gaps.length === 0) {
    return null;
  }

  const visibleGaps = expanded ? gaps : gaps.slice(0, 2);
  const hiddenCount = gaps.length - 2;

  return (
    <div
      className={`rounded-xl border border-[color:var(--color-warning-200)] bg-[color:var(--color-warning-25)] p-4 ${className}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-warning-100)' }}
        >
          <svg
            className="w-4 h-4 text-[color:var(--color-warning-600)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-[13px] font-semibold text-[color:var(--color-warning-800)]">
            Information Not Found
          </h4>
          <p className="text-[12px] text-[color:var(--color-warning-600)] mt-0.5">
            I couldn't find the following in your workspace:
          </p>
        </div>
      </div>

      {/* Gaps list */}
      <div className="space-y-2 ml-11">
        {visibleGaps.map((gap, index) => (
          <div
            key={`gap-${index}`}
            className="flex items-start gap-2 p-2.5 rounded-lg bg-white/60 border border-[color:var(--color-warning-100)]"
          >
            <div className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-[color:var(--color-warning-100)] text-[color:var(--color-warning-600)]">
              <span className="text-[10px] font-bold">{index + 1}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-[color:var(--color-neutral-800)]">
                {gap.topic}
              </p>
              <p className="text-[11px] text-[color:var(--color-neutral-500)] mt-0.5">
                {gap.why_needed}
              </p>
              {gap.suggested_source && (
                <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-[color:var(--color-warning-600)]">
                  <SourceSuggestionIcon source={gap.suggested_source} />
                  <span>{getSourceLabel(gap.suggested_source)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Show more/less */}
      {gaps.length > 2 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="ml-11 mt-2 text-[12px] text-[color:var(--color-warning-600)] hover:text-[color:var(--color-warning-700)] hover:underline transition-colors"
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mt-4 ml-11">
        {onUploadDocument && (
          <button
            type="button"
            onClick={onUploadDocument}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border border-[color:var(--color-warning-300)] bg-white text-[color:var(--color-warning-700)] hover:bg-[color:var(--color-warning-50)] transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload document
          </button>
        )}
        {onSuggestExpert && (
          <button
            type="button"
            onClick={onSuggestExpert}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border border-[color:var(--color-warning-300)] bg-white text-[color:var(--color-warning-700)] hover:bg-[color:var(--color-warning-50)] transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Find an expert
          </button>
        )}
      </div>
    </div>
  );
};

export default KnowledgeGapIndicator;

